/**
 * Sales schemas (Vendes i Expedició) — comanda de venda capçalera + línies,
 * expedició (shipment = albarà de venda) + línies, plus the derived read
 * shapes (per-line fulfillment and per-article committed stock).
 *
 * Convention (schemas/index.ts): `Entity` = read shape (tenant_id, timestamps,
 * soft-delete, sync fields); `*Input` = client write payload, `.strict()`,
 * NEVER accepts tenant_id, omits server-generated values (order_number,
 * shipment_number — provisional offline, reconciled on sync). The comanda's
 * "quantitat pendent de servir" is DERIVED (decision Q3 = backorder), not a
 * stored per-line accumulator — see SalesOrderLineFulfillment (the
 * sales_order_line_fulfillment view) and domain/sales.ts.
 */

import { z } from 'zod';
import {
  SALES_ORDER_STATUSES,
  SALES_ORDER_TYPES,
  SALES_ORDER_ORIGINS,
  SHIPMENT_STATUSES,
} from '../enums/index.js';
import {
  uuid,
  nonEmptyString,
  timestamptz,
  dateString,
  nonNegativeQuantity,
  money,
  timestampFields,
  softDeleteFields,
  syncFields,
} from './common.js';

/** A strictly positive quantity (DB CHECKs ordered_quantity/quantity > 0). */
const positiveQuantity = z.number().finite().positive();

// ── SalesOrderLine ───────────────────────────────────────────────────────────

/**
 * One line of a comanda de venda. Only articles with `is_sold = true` may
 * appear (producte acabat AND semielaborat venible — enforced by the pickers
 * and the write paths; the DB cannot express a cross-table CHECK).
 * `sales_unit_id` references the packaging units master; null = the article
 * base unit (radial factor in article_packaging_units). `unit_price` is the
 * optional agreed price (decision Q4 — no tariff engine in O1). No stored
 * shipped accumulator: fulfillment is derived (see SalesOrderLineFulfillment).
 */
export const SalesOrderLine = z
  .object({
    id: uuid,
    tenant_id: uuid,
    sales_order_id: uuid,
    article_id: uuid,
    ordered_quantity: nonNegativeQuantity,
    sales_unit_id: uuid.nullable(),
    unit_price: money.nullable(),
    sort_order: z.number().int(),
    ...syncFields,
    ...timestampFields,
    ...softDeleteFields,
  })
  .strict();
export type SalesOrderLine = z.infer<typeof SalesOrderLine>;

/** Create/edit payload for a single comanda de venda line. */
export const SalesOrderLineInput = z
  .object({
    article_id: uuid,
    ordered_quantity: positiveQuantity,
    sales_unit_id: uuid.nullable().optional(),
    unit_price: money.nullable().optional(),
    sort_order: z.number().int().min(0).default(0),
  })
  .strict();
export type SalesOrderLineInput = z.infer<typeof SalesOrderLineInput>;

// ── SalesOrder ───────────────────────────────────────────────────────────────

/**
 * Comanda de venda (capçalera). `order_number` (CV-…) is server-assigned
 * (trigger, never manual — the app shows a provisional number offline), so the
 * read shape includes it but the input schemas do not. `order_type` is always
 * 'mts' in O1 (the MTO branch activates in O2); `origin` records where the
 * comanda was born (backoffice / planta / integration hook).
 */
export const SalesOrder = z
  .object({
    id: uuid,
    tenant_id: uuid,
    order_number: nonEmptyString,
    client_id: uuid,
    order_type: z.enum(SALES_ORDER_TYPES),
    origin: z.enum(SALES_ORDER_ORIGINS),
    order_date: dateString,
    delivery_date: dateString.nullable(),
    /** Designed by the spec; intentionally unused in the O1 UI. */
    preparation_date: dateString.nullable(),
    delivery_address: z.string().trim().min(1).nullable(),
    status: z.enum(SALES_ORDER_STATUSES),
    notes: z.string().trim().min(1).nullable(),
    created_by: uuid.nullable(),
    ...syncFields,
    ...timestampFields,
    ...softDeleteFields,
  })
  .strict();
export type SalesOrder = z.infer<typeof SalesOrder>;

/**
 * Create payload for a comanda de venda (capçalera + línies). tenant_id and
 * order_number are NEVER accepted from the client. At least one line is
 * required. `origin` defaults to 'backoffice'; the planta entry flow passes
 * 'planta'. `order_type` defaults to 'mts' (O1 only executes the MTS branch).
 */
export const SalesOrderCreateInput = z
  .object({
    client_id: uuid,
    order_type: z.enum(SALES_ORDER_TYPES).default('mts'),
    origin: z.enum(SALES_ORDER_ORIGINS).default('backoffice'),
    order_date: dateString.optional(),
    delivery_date: dateString.nullable().optional(),
    delivery_address: nonEmptyString.nullable().optional(),
    notes: nonEmptyString.nullable().optional(),
    lines: z.array(SalesOrderLineInput).min(1),
  })
  .strict();
export type SalesOrderCreateInput = z.infer<typeof SalesOrderCreateInput>;

/**
 * Header update payload (edit an existing comanda / advance its status). Lines
 * are edited through their own SalesOrderLineInput writes. Every field is
 * optional so a partial patch persists only what changed; `.strict()` rejects a
 * forged tenant_id / order_number / order_type flip.
 */
export const SalesOrderUpdateInput = z
  .object({
    client_id: uuid.optional(),
    order_date: dateString.optional(),
    delivery_date: dateString.nullable().optional(),
    delivery_address: nonEmptyString.nullable().optional(),
    status: z.enum(SALES_ORDER_STATUSES).optional(),
    notes: nonEmptyString.nullable().optional(),
  })
  .strict();
export type SalesOrderUpdateInput = z.infer<typeof SalesOrderUpdateInput>;

// ── Shipment (expedició = albarà de venda) ───────────────────────────────────

/**
 * Expedició / albarà de venda (capçalera). Decision Q3: 1 expedició = 1 albarà;
 * N shipments per comanda (partials, backorder). `shipment_number` (AV-…) is
 * server-assigned; `client_id` is denormalized from the comanda for direct
 * lookup, PDF and lot↔client trace; `prepared_by` is resolved server-side from
 * the caller JWT at sync (client value never trusted).
 */
export const Shipment = z
  .object({
    id: uuid,
    tenant_id: uuid,
    shipment_number: nonEmptyString,
    sales_order_id: uuid,
    client_id: uuid,
    status: z.enum(SHIPMENT_STATUSES),
    shipped_at: timestamptz,
    notes: z.string().trim().min(1).nullable(),
    prepared_by: uuid.nullable(),
    ...syncFields,
    ...timestampFields,
    ...softDeleteFields,
  })
  .strict();
export type Shipment = z.infer<typeof Shipment>;

/**
 * One picked line of an expedició. The lot link is the paper trace:
 * `finished_lot_id` for producte acabat / semielaborat venible,
 * `supplier_lot_id` for resold MP — at most one of the two (DB CHECK). Both
 * null only for `is_stock_tracked = false` articles (no movements either); the
 * lot-required rule for tracked articles is enforced by the write paths (the
 * article flag is not part of this payload).
 */
export const ShipmentLine = z
  .object({
    id: uuid,
    tenant_id: uuid,
    shipment_id: uuid,
    sales_order_line_id: uuid.nullable(),
    article_id: uuid,
    quantity: nonNegativeQuantity,
    sales_unit_id: uuid.nullable(),
    finished_lot_id: uuid.nullable(),
    supplier_lot_id: uuid.nullable(),
    ...syncFields,
    ...timestampFields,
    ...softDeleteFields,
  })
  .strict();
export type ShipmentLine = z.infer<typeof ShipmentLine>;

/** Create payload for a single expedició line (inside ShipmentConfirmInput). */
export const ShipmentLineInput = z
  .object({
    /** Nullable in schema for future direct shipments; REQUIRED in the O1 flow. */
    sales_order_line_id: uuid,
    article_id: uuid,
    quantity: positiveQuantity,
    sales_unit_id: uuid.nullable().optional(),
    finished_lot_id: uuid.nullable().optional(),
    supplier_lot_id: uuid.nullable().optional(),
  })
  .strict()
  .refine((l) => !(l.finished_lot_id != null && l.supplier_lot_id != null), {
    message: 'a shipment line references at most one lot (finished OR supplier)',
  });
export type ShipmentLineInput = z.infer<typeof ShipmentLineInput>;

/**
 * The atomic picking gesture (confirm an expedició): capçalera + picked lines.
 * Validated identically by the app builder and the sync-push edge function.
 * shipment_number is NEVER accepted (provisional offline, trigger-assigned on
 * the server, reconciled back).
 */
export const ShipmentConfirmInput = z
  .object({
    sales_order_id: uuid,
    shipped_at: timestamptz.optional(),
    notes: nonEmptyString.nullable().optional(),
    lines: z.array(ShipmentLineInput).min(1),
  })
  .strict();
export type ShipmentConfirmInput = z.infer<typeof ShipmentConfirmInput>;

// ── Derived read shapes (views) ──────────────────────────────────────────────

/**
 * Read shape of the sales_order_line_fulfillment view: ordered / shipped /
 * pending quantities per line, all expressed in the article base unit. Feeds
 * the derived comanda status (domain/sales.ts) and the picking screens.
 */
export const SalesOrderLineFulfillment = z
  .object({
    tenant_id: uuid,
    sales_order_id: uuid,
    sales_order_line_id: uuid,
    article_id: uuid,
    ordered_quantity: nonNegativeQuantity,
    sales_unit_id: uuid.nullable(),
    ordered_base_quantity: nonNegativeQuantity,
    shipped_base_quantity: nonNegativeQuantity,
    pending_base_quantity: nonNegativeQuantity,
  })
  .strict();
export type SalesOrderLineFulfillment = z.infer<typeof SalesOrderLineFulfillment>;

/**
 * Read shape of the article_committed_stock view (decision Q2 — SOFT
 * committed-stock indicator): per article, the pending-to-serve total across
 * firm comandes (confirmed / preparing). Purely informative; never a lock.
 */
export const ArticleCommittedStock = z
  .object({
    tenant_id: uuid,
    article_id: uuid,
    committed_base_quantity: nonNegativeQuantity,
  })
  .strict();
export type ArticleCommittedStock = z.infer<typeof ArticleCommittedStock>;
